// Scheduling settings: the Client Sessions card (booking config, and which set of hours client sessions use).
// Reads/writes coaches.settings.scheduling via Netlify functions
// (NOT localStorage — needs to be authoritative for the client booking page).
//
// ⚠️ THE DAYS, TIMES, "HOW SOON" AND "HOW FAR AHEAD" ARE NOT ON THIS CARD ANY MORE (Clayton, 10/16). They are a named
// set of hours (modules/availability-sets.js, listed on Settings → Availability) and this card PICKS the one client
// sessions use, with a read-out and a button that opens the same editing window. Three things follow from that:
// - `weekly`, `earliestBookingHours` and `latestBookingDays` still ride back on every Save, exactly as loaded
//   (readFromFields spreads the loaded object). coach-availability-update refuses a CHANGED value once a coach has sets
//   ("Your booking hours now live in Settings → Availability"), so a card opened before that, in an old tab, cannot
//   write old hours over a set; an unchanged value passes.
// - The picked set goes as `clientSessionsTemplateId` ONLY when the coach changed it (the server checks it is theirs).
// - A set that did not load is not guessed: the Hours part says so and locks, the rest of the card still works.
//
// ⚠️ "CLIENTS ASK, YOU CONFIRM" (session requests, Clayton 10/16) IS NOT PART OF `scheduling`. It is the coach's switch for
// every client whose booking waits for their OK (the Session Tracker answers it for one client), and it is saved as its OWN
// top-level `sessionApproval` beside `scheduling`, ONLY when the coach changed it. Inside `scheduling` it would be reset by any
// Save from a tab opened before it was turned on (this card sends `scheduling` whole, and the server merges it over the
// defaults), and sent every time it would write the box's value over a switch changed from another tab.

import { showToast } from './utils.js';
import { callFunction } from './api.js';
import { loadHourSets, openHoursDialog, hoursSummary, limitsSummary } from './availability-sets.js';
import { setCoachWideApproval } from './session-requests/store.js';

let _scheduling = null;
let _saving = false;
// True once the coach's own settings arrived; until then neither card on this
// tab may save (see lockUnloadedCard).
let _loaded = false;
// Hours (sets). `_sets` is [] until they arrive; `_sentPick` is the set client sessions used when the card last loaded or
// saved ('' is the virtual "My hours"), so a Save sends the picker only when it differs from that.
/** @type {any[]} */
let _sets = [];
let _setsLoaded = false;
let _sentPick = '';
// "Clients ask, you confirm": the value the server held when the card loaded or last saved, so a Save sends the switch only when
// the coach changed it.
let _sentApproval = false;
const $approval = () => /** @type {HTMLInputElement | null} */ (document.getElementById('s-sched-approval'));

async function fetchScheduling() {
  const json = await callFunction('coach-availability-get', {});
  // The server always sends the settings (merged with its defaults for a coach
  // who never saved any), and callFunction hands back {} for an empty reply,
  // so an answer without them means they did not load.
  if (!json?.scheduling) throw new Error('coach-availability-get answered without the settings');
  return {
    scheduling: json.scheduling,
    // false only when the server says no zone was ever saved (the zone it
    // sent is then the default); an older answer without it counts as saved.
    timezoneSaved: json.timezoneSaved !== false,
    cycleEndNudgeEnabled: json.cycleEndNudgeEnabled !== false,
    cycleEndNudgeDays: Number(json.cycleEndNudgeDays) || 10,
    // Off unless exactly true, the server's own reading (an older answer without it is off)
    sessionApproval: json.sessionApproval === true,
  };
}

/**
 * @param {any} scheduling
 * @param {string|null} [pickedSet] the set client sessions should use, only when it changed
 * @param {boolean|null} [approval] "Clients ask, you confirm", only when it changed (top-level, never inside `scheduling`)
 */
async function saveScheduling(scheduling, pickedSet = null, approval = null) {
  const json = await callFunction('coach-availability-update', {
    scheduling,
    ...(pickedSet ? { clientSessionsTemplateId: pickedSet } : {}),
    ...(typeof approval === 'boolean' ? { sessionApproval: approval } : {}),
  });
  return json;
}

async function saveNudgeSettings(payload) {
  const json = await callFunction('coach-availability-update', payload);
  return json;
}

// ── Hours: which set client sessions use ─────────────────────────────────────────────────────────────────────────────
const $pick = () => /** @type {HTMLSelectElement | null} */ (document.getElementById('s-sched-hours-set'));

/** The set the picker shows (the virtual "My hours" has id null and the value ''). */
function pickedSet() {
  const v = $pick()?.value ?? '';
  return _sets.find((x) => (x.id || '') === v) || null;
}

/** Fill the picker and the read-out under it. `keep` is the value to leave picked if that set still exists. */
function paintHours(keep) {
  const sel = $pick();
  if (!sel) return;
  sel.innerHTML = '';
  for (const set of _sets) {
    const opt = document.createElement('option');
    opt.value = set.id || '';
    opt.textContent = set.name;
    sel.appendChild(opt);
  }
  sel.value = _sets.some((x) => (x.id || '') === keep) ? keep : (_sentPick || _sets[0]?.id || '');
  sel.disabled = !_setsLoaded || !_loaded;
  const edit = /** @type {HTMLButtonElement | null} */ (document.getElementById('btn-sched-hours-edit'));
  if (edit) edit.disabled = !_setsLoaded || !_loaded;
  paintReadout();
}

/** What the picked set says, in words: its days and times, then how soon and how far ahead. */
function paintReadout() {
  const box = document.getElementById('s-sched-hours-read');
  if (!box) return;
  const set = pickedSet();
  box.textContent = '';
  if (!set) return;
  for (const [cls, words] of [['sched-hours-days', hoursSummary(set.weekly)], ['sched-hours-limits', limitsSummary(set)]]) {
    const line = document.createElement('p');
    line.className = cls;
    line.textContent = words;
    box.appendChild(line);
  }
}

/**
 * Read the sets again and repaint the picker. `keep` is the picked value to hold on to (the coach may have changed the
 * picker and not saved yet); otherwise the picker follows the set the server says client sessions use.
 */
async function loadHours(keep) {
  const { sets, clientSessionsTemplateId } = await loadHourSets();
  _sets = sets;
  // ⚠️ WHAT THE SERVER HOLDS, NOT WHAT THE PICKER SHOWS. An empty pointer beside real sets (a save that stopped
  // half way) means the coach's old saved hours, not the oldest set: the picker shows the oldest, and because that
  // differs from '' the next Save sends it and puts client sessions on it. The virtual set is the only one with no id.
  _sentPick = clientSessionsTemplateId || '';
  _setsLoaded = true;
  const err = document.getElementById('s-sched-hours-error');
  if (err) err.hidden = true;
  paintHours(keep);
  // The picker is one of the fields app.js's dirty tracker snapshots, and it was filled after that snapshot was
  // taken: take it again, unless the coach has already changed something on the card.
  const btn = /** @type {any} */ (document.getElementById('btn-save-scheduling'));
  if (btn?.disabled) btn._dirtyReset?.();
}

/** The sets did not load: say so, and leave the picker locked and empty. Nothing is guessed. */
function lockHours() {
  _sets = [];
  _setsLoaded = false;
  const err = document.getElementById('s-sched-hours-error');
  if (err) err.hidden = false;
  const sel = $pick();
  if (sel) { sel.innerHTML = ''; sel.disabled = true; }
  const edit = /** @type {HTMLButtonElement | null} */ (document.getElementById('btn-sched-hours-edit'));
  if (edit) edit.disabled = true;
  document.getElementById('s-sched-hours-read')?.replaceChildren();
}

/** Changing the pick repaints the read-out and lights Save (app.js's tracker also snapshots the picker). */
function onPick() {
  paintReadout();
  const btn = document.getElementById('btn-save-scheduling');
  if (btn) /** @type {HTMLButtonElement} */ (btn).disabled = false;
}

function editPickedHours() {
  const set = pickedSet();
  if (!set) return;
  openHoursDialog({
    set,
    mode: 'edit',
    // The hours changed (and My hours may have become a real saved set): read them again, keeping what is picked.
    onSaved: async () => {
      try { await loadHours($pick()?.value ?? ''); } catch (e) { console.warn('[scheduling] hours reload failed', e); lockHours(); }
    },
  });
}

/** "See all your hours": switch to Settings → Availability. */
function openAvailabilityTab() {
  /** @type {HTMLElement | null} */ (document.querySelector('.settings-tab-btn[data-tab="availability"]'))?.click();
}

function applyToFields(s) {
  document.getElementById('s-sched-enabled').checked = !!s.enabled;
  document.getElementById('s-sched-session-min').value = s.defaultSessionMin ?? 60;
  document.getElementById('s-sched-buffer-min').value = s.bufferMin ?? 0;
  document.getElementById('s-sched-notice-hours').value = s.noticeHours ?? 24;
  document.getElementById('s-sched-max-per-day').value = s.maxPerDay ?? 0;
  document.getElementById('s-sched-location-label').value = s.locationLabel || '';
  document.getElementById('s-sched-location-address').value = s.locationAddress || '';
  const homeEl = /** @type {HTMLInputElement|null} */ (document.getElementById('s-sched-home-address'));
  if (homeEl) homeEl.value = s.homeAddress || '';
  const startEl = /** @type {HTMLSelectElement|null} */ (document.getElementById('s-sched-day-starts-from'));
  if (startEl) startEl.value = s.dayStartsFrom === 'home' ? 'home' : 'work';
  const travelEl = document.getElementById('s-sched-travel-time');
  if (travelEl) travelEl.value = ['off', 'show', 'block'].includes(s.travelTime) ? s.travelTime : 'show';
  document.getElementById('s-sched-booking-message').value = s.bookingMessage || '';
}

// ⚠️ THE ZONE EVERY BOOKING RUNS IN, SHOWN HERE AND CHOSEN ON APPEARANCE.
// This card first had no time zone field (everyone ran on Central), then its
// own select, which made two time zone settings: this one for booking, and
// Settings → Appearance for the CRM's dates. Appearance is the only place to
// choose now (modules/time-zone-setting.js saves both). This card names the
// zone, says where to change it, and its Save never sends one (readFromFields),
// so a card opened before the zone changed cannot write the old one back.
function applyTimeZone(stored, saved) {
  const name = document.getElementById('s-sched-tz-name');
  const hint = document.getElementById('s-sched-tz-hint');
  // Nothing to name when the settings did not load (the card then shows none
  // of them, see lockUnloadedCard).
  if (name) name.textContent = stored || 'your time zone';
  if (hint) {
    hint.textContent = saved
      ? ''
      : `You haven't chosen a time zone yet, so bookings run on ${stored}. Choose yours in Settings → Appearance → Time Zone.`;
  }
}

/** "Change it in Settings → Appearance": switch to that tab, at the Time Zone card. */
function openTimeZoneSetting() {
  /** @type {HTMLElement | null} */ (document.querySelector('.settings-tab-btn[data-tab="appearance"]'))?.click();
  document.getElementById('s-timezone')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
}

function readFromFields() {
  // No `timezone`: the server keeps the stored one when it is left out (see
  // applyTimeZone above for why this card must never send it).
  const { timezone: _zone, ...rest } = _scheduling;
  // ⚠️ `rest` CARRIES weekly, earliestBookingHours AND latestBookingDays BACK EXACTLY AS LOADED. This card no longer edits
  // them (they are the picked set's now), and the server's stale-tab guard (coach-availability-update.js) passes a value
  // that is unchanged and refuses one that changed. Do not "clean them out" of the save, and do not read them from boxes.
  return {
    ...rest,
    enabled: document.getElementById('s-sched-enabled').checked,
    defaultSessionMin: +document.getElementById('s-sched-session-min').value || 60,
    bufferMin: +document.getElementById('s-sched-buffer-min').value || 0,
    noticeHours: +document.getElementById('s-sched-notice-hours').value || 24,
    maxPerDay: +document.getElementById('s-sched-max-per-day').value || 0,
    locationLabel: document.getElementById('s-sched-location-label').value.trim(),
    locationAddress: document.getElementById('s-sched-location-address').value.trim(),
    // Left out of the page = keep the stored one, so a card drawn before this
    // field existed can never save it blank.
    homeAddress: /** @type {HTMLInputElement|null} */ (document.getElementById('s-sched-home-address'))?.value.trim() ?? (_scheduling.homeAddress || ''),
    // Where the day starts for the calendar's first drive (10/20); left out of the page = keep the stored one
    dayStartsFrom: /** @type {HTMLSelectElement|null} */ (document.getElementById('s-sched-day-starts-from'))?.value || _scheduling.dayStartsFrom || 'work',
    travelTime: document.getElementById('s-sched-travel-time')?.value || _scheduling.travelTime || 'show',
    bookingMessage: document.getElementById('s-sched-booking-message').value.trim(),
  };
}

// ⚠️ A CARD WHOSE SETTINGS DID NOT LOAD NEVER SAVES. A failed load used to
// fill both cards on this tab with built-in defaults (Mon–Fri 6–11am and
// 3–8pm, 60-minute sessions, online booking off; the reminder on at 10 days)
// shown as if they were the coach's own, and either Save wrote them over the
// coach's real settings. Now the card says it did not load and shows nothing:
// no grey hint text in an empty box (60, 24, "Revolution Fitness") and no
// first option standing in for the coach's choice. Its fields are locked, so
// no edit can fire the input event that lights its Save
// (initSettingsDirtyTracking in app.js), and the save handlers refuse on their
// own as well (_loaded).
function lockUnloadedCard(saveId, lineId) {
  const save = /** @type {HTMLButtonElement | null} */ (document.getElementById(saveId));
  save?.closest('.settings-card')?.querySelectorAll('input, select, textarea').forEach((el) => {
    /** @type {HTMLInputElement} */ (el).disabled = true;
    if (el instanceof HTMLSelectElement) el.selectedIndex = -1;
    else /** @type {HTMLInputElement} */ (el).placeholder = '';
  });
  if (save) save.disabled = true;
  const line = document.getElementById(lineId);
  if (line) line.hidden = false;
}

let _initialized = false;

export async function initScheduling() {
  if (_initialized) {
    // Back on the tab (it calls this each time it opens): the sets may have changed since, on Settings → Availability
    // or a Booked page (a new one, a rename, a removal). Read them again and keep what the coach has picked.
    if (_loaded && !_saving) {
      try { await loadHours($pick()?.value ?? ''); } catch (e) { console.warn('[scheduling] hours reload failed', e); }
    }
    return;
  }
  _initialized = true;
  let nudgeState = null;
  let timezoneSaved = true;
  try {
    const res = await fetchScheduling();
    _scheduling = res.scheduling;
    timezoneSaved = res.timezoneSaved;
    _sentApproval = res.sessionApproval;
    nudgeState = {
      cycleEndNudgeEnabled: res.cycleEndNudgeEnabled,
      cycleEndNudgeDays: res.cycleEndNudgeDays,
    };
    _loaded = true;
  } catch (e) {
    console.warn('[scheduling] load failed, so neither card can save', e);
  }
  applyTimeZone(_loaded ? _scheduling.timezone : '', timezoneSaved);
  document.getElementById('btn-sched-tz-change')?.addEventListener('click', openTimeZoneSetting);
  document.getElementById('s-sched-hours-set')?.addEventListener('change', onPick);
  document.getElementById('btn-sched-hours-edit')?.addEventListener('click', editPickedHours);
  document.getElementById('btn-sched-hours-all')?.addEventListener('click', openAvailabilityTab);
  if (!_loaded) {
    lockUnloadedCard('btn-save-scheduling', 's-sched-load-error');
    lockUnloadedCard('btn-save-nudge', 's-nudge-load-error');
    // The picker is a <select> inside the card, so the lock above took it too; "Edit these hours" starts disabled.
  } else {
    applyToFields(_scheduling);
    const approvalBox = $approval();
    if (approvalBox) approvalBox.checked = _sentApproval;
    // The Session Tracker's Booking choice names the coach's default; this card just told us what it is
    setCoachWideApproval(_sentApproval);

    // Apply cycle-end nudge fields
    const nudgeEnabled = document.getElementById('s-nudge-enabled');
    const nudgeDays = document.getElementById('s-nudge-days');
    if (nudgeEnabled) nudgeEnabled.checked = nudgeState.cycleEndNudgeEnabled;
    if (nudgeDays) nudgeDays.value = nudgeState.cycleEndNudgeDays;
  }

  document.getElementById('btn-save-scheduling')?.addEventListener('click', async () => {
    if (_saving || !_loaded) return;
    // ⚠️ A 0-HOUR NOTICE WAS SAVED AS 24 AND CALLED SAVED (readFromFields'
    // `|| 24`). 0 never worked anywhere: the server's can't-make-it and
    // standing-session paths and the client app read 0 as 24. Blank still
    // means the default 24.
    const notice = document.getElementById('s-sched-notice-hours')?.value.trim();
    if (notice && !(Number(notice) >= 1)) {
      showToast('Cancel notice must be at least 1 hour.', 'error');
      return;
    }
    // ⚠️ HOME WITH NO HOME ADDRESS WOULD START THE DAY NOWHERE: the first drive would quietly vanish (day-data.js dayStart)
    const startFrom = /** @type {HTMLSelectElement|null} */ (document.getElementById('s-sched-day-starts-from'))?.value;
    const homeBox = /** @type {HTMLInputElement|null} */ (document.getElementById('s-sched-home-address'));
    if (startFrom === 'home' && homeBox && !homeBox.value.trim()) {
      showToast('Add your home address to start your day from home.', 'error');
      return;
    }
    const btn = document.getElementById('btn-save-scheduling');
    _saving = true;
    let ok = false;
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      _scheduling = readFromFields();
      // The picker goes only when the coach changed it (and never the virtual My hours, which is the only choice then).
      const picked = $pick()?.value ?? '';
      const pickChanged = _setsLoaded && picked !== '' && picked !== _sentPick;
      // ⚠️ THE SWITCH GOES ONLY WHEN THE COACH CHANGED IT (see the top of this file), as its own top-level key
      const approvalBox = $approval();
      const approvalChanged = !!approvalBox && approvalBox.checked !== _sentApproval;
      const answer = await saveScheduling(_scheduling, pickChanged ? picked : null, approvalChanged ? /** @type {HTMLInputElement} */ (approvalBox).checked : null);
      const saved = answer.scheduling;
      if (saved) {
        _scheduling = saved;
        applyTimeZone(_scheduling.timezone, true);
      }
      // What the server says client sessions use now (it echoes the saved pick); an older answer without it means the
      // pick we sent, or the one we had.
      if (pickChanged) _sentPick = answer.clientSessionsTemplateId || picked;
      // What the server says is stored (it echoes the switch); an older answer without it means what we sent
      if (approvalChanged) {
        _sentApproval = typeof answer.sessionApproval === 'boolean' ? answer.sessionApproval : /** @type {HTMLInputElement} */ (approvalBox).checked;
        setCoachWideApproval(_sentApproval);
      }
      showToast('Client session settings saved.', 'success');
      ok = true;
    } catch (e) {
      showToast(e.message || 'Failed to save', 'error');
    } finally {
      _saving = false;
      btn.disabled = false;
      btn.textContent = 'Save Client Session Settings';
      // ⚠️ AFTER the re-enable above, not inside the try — _dirtyReset()
      // disables the button, and this finally would immediately undo it.
      if (ok) btn._dirtyReset?.();
    }
  });

  document.getElementById('btn-save-nudge')?.addEventListener('click', async () => {
    if (!_loaded) return;
    const btn = document.getElementById('btn-save-nudge');
    let ok = false;
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      await saveNudgeSettings({
        cycleEndNudgeEnabled: !!document.getElementById('s-nudge-enabled')?.checked,
        cycleEndNudgeDays: parseInt(document.getElementById('s-nudge-days')?.value, 10) || 10,
      });
      showToast('Reminder settings saved.', 'success');
      ok = true;
    } catch (e) {
      showToast(e.message || 'Failed to save', 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save Reminder Settings';
      if (ok) btn._dirtyReset?.();
    }
  });

  // The sets load last, beside the rest of the card rather than before it: if they fail the Hours part says so and
  // locks, and the rest of the card (loaded from its own answer) still works.
  if (_loaded) {
    try { await loadHours(''); } catch (e) { console.warn('[scheduling] hours load failed', e); lockHours(); }
  }
}

// Expose for app.js to call when Sessions subtab is opened
window.initSchedulingSettings = initScheduling;
